/**
 * Server- and client-readable runtime configuration — one umbrella
 * for every value that the server knows at request time and the
 * client needs to read after hydration.
 *
 * Why this exists
 * ---------------
 * Next.js inlines every `NEXT_PUBLIC_*` env var into the build
 * output at build time. That's fine for a per-environment build
 * (Vercel pattern), but breaks our deployment model where ONE
 * portal image is promoted unchanged across `local → dev → staging →
 * production`. With `NEXT_PUBLIC_*`, the value baked at build time
 * leaks into every environment — `dev` would serve `production`
 * values.
 *
 * The fix:
 *   - Server-side code reads `process.env.X` directly. Plain (non-
 *     `NEXT_PUBLIC_`) env vars are resolved at REQUEST time, so the
 *     same image picks up per-env config from kustomize ConfigMaps.
 *   - Client-side code reads `getRuntimeConfig().X`, which reads
 *     from `window.__ROBUTLER_CONFIG__` — populated by the root
 *     layout via `<script>` injection. The layout runs server-side
 *     per request, so the injected JSON is the live env value at
 *     the moment the page was rendered.
 *
 * Sub-sections
 * ------------
 * Domain modules (analytics, …) namespace under a top-level key. Each
 * domain owns its own shape; this module is the umbrella. To add a
 * new domain: extend `RuntimeConfig`, populate it in
 * `readServerRuntimeConfig`, and export a domain-specific reader
 * that delegates to `getRuntimeConfig().<domain>`.
 *
 * What goes here
 * --------------
 * Only PUBLIC values that are safe to ship in the HTML source:
 * URLs, hostnames, feature flags, analytics IDs. Never secrets,
 * never per-user data — anyone can View Source.
 */

import type { AnalyticsConfig } from './analytics/config';
import { type ConsentRegime, resolveConsentRegime } from './consent';
import { SIGN_IN_PROVIDERS, type SignInProvider } from './auth/sign-in-providers';

export interface RuntimeConfig {
  /**
   * Apex portal base URL — `https://robutler.ai`,
   * `https://staging.robutler.net`, `https://macbook-air.tail<id>.ts.net`.
   * Set per env via the `BASE_URL` env var.
   */
  baseUrl: string;
  /**
   * Sandbox-host origin for iframe-embed isolation (Plan v3-05).
   * Lives on a different eTLD+1 from the apex (`robutler.app` vs
   * `robutler.ai`) so apex cookies can't leak via `Domain=.robutler.ai`
   * scoping or SameSite policies.
   *
   *   - production: `https://robutler.app`
   *   - dev:        `https://dev.robutler.app`
   *   - staging:    `https://staging.robutler.app`
   *   - local:      `null` — falls back to apex-srcdoc renderer
   *     (no localStorage in widgets, but the iframe still loads)
   *
   * Set per env via `SANDBOX_ORIGIN`. Empty / unset → null.
   */
  sandboxOrigin: string | null;
  /**
   * Is the per-content origin FAMILY switched on for this environment
   * (ADR-0039)? When true, each app is served from
   * `<contentId>.<sandboxOrigin host>` instead of from `sandboxOrigin` itself,
   * and the browser's own origin rule partitions their storage.
   *
   * DEFAULT FALSE, AND THAT IS DELIBERATE. Flipping it moves every app to a new
   * origin, and a new origin means a new `localStorage` and IndexedDB bucket:
   * anything an app had persisted for a user appears to have been reset. So it
   * is a per-environment switch that is turned on once, knowingly, and not a
   * flag day. Set via `SANDBOX_PER_CONTENT=1`.
   */
  sandboxPerContent: boolean;
  /**
   * Cloudflare Turnstile PUBLIC site key for the guest-mint bot challenge.
   * Public (rendered in the widget, safe to ship in HTML) so it lives here,
   * not in `portal-secrets`. Empty string disables the client widget — the
   * mint endpoint then allows when its `TURNSTILE_SECRET_KEY` is also unset
   * (dev) and fails closed when the secret IS set (prod misconfig guard).
   * Set per env via the non-secret `TURNSTILE_SITE_KEY` (portal-config
   * ConfigMap); the secret half is `TURNSTILE_SECRET_KEY` in portal-secrets.
   */
  turnstileSiteKey: string;
  /**
   * WebMCP (ADR-0049): does the workspace page offer its open apps' commands
   * to the browser's own assistant? Per environment, via `WEBMCP_TOOLS`:
   * `1` on, `0` off. Unset, it is on only under the dev server
   * (`NODE_ENV=development`, which no deployed environment has), so the local
   * loop needs no pod change and every deployed environment stays dark until
   * someone sets it. That reads `NODE_ENV` to find the DEV SERVER, not to
   * find production; `isProductionRuntime()` remains the only production test.
   */
  webmcpTools: boolean;
  /**
   * The Chrome origin trial token for THIS environment's apex origin
   * (`WEBMCP_ORIGIN_TRIAL_TOKEN`, non-secret: a trial token is bound to its
   * origin and is meant to ship in the page). When set and the API is absent,
   * the bridge appends the `origin-trial` meta tag at runtime, because the
   * root layout is static and a per-environment value cannot be baked into
   * it. Empty string: no meta, and the API exists only behind the person's
   * own Chrome flag. One token per apex origin; the trial ends with Chrome 156.
   */
  webmcpOriginTrialToken: string;
  /**
   * The sign-in providers THIS environment has credentials for, in the
   * platform's order (`SIGN_IN_PROVIDERS`, lib/auth/sign-in-providers.ts).
   * Computed server-side from the PRESENCE of each provider's client id and
   * secret pair, never from their values, which never leave the pod. The
   * sign-in screens draw a provider's button only when it is listed here
   * (app-sign-in-and-onboarding.md, section 5.6: "The button shows only
   * where the credentials are set"), so until LinkedIn's pair is set on an
   * environment, an app that asks for LinkedIn first gets Google first.
   * Public by construction: which providers a site offers is on its sign-in
   * page for anyone to read.
   */
  signInProviders: SignInProvider[];
  /**
   * Analytics IDs + adapter wiring. Domain-owned by
   * `lib/analytics/config.ts`; read via `getAnalyticsConfig()`
   * (which delegates here).
   */
  analytics: AnalyticsConfig;
  /**
   * Which consent law applies to THIS request's visitor, resolved from the
   * ingress `x-geo-country` header. Unlike every other field here it is
   * per-request rather than per-env, so it is passed into
   * `runtimeConfigInjectScript()` by the root layout instead of being read
   * from `process.env`.
   *
   * Public and non-identifying: it is a coarse legal-regime label, not a
   * location. `'opt-in'` whenever the country is unknown — see
   * `resolveConsentRegime`.
   */
  consentRegime: ConsentRegime;
}

/**
 * The environment variable PAIR each sign-in provider's start route and
 * callback read (lib/auth/google.ts, lib/auth/x.ts,
 * lib/auth/discord-identity.ts, and LinkedIn's identity module beside them).
 * Only the names live here: `configuredSignInProviders` tests presence and
 * never copies a value anywhere.
 */
const SIGN_IN_PROVIDER_CREDENTIAL_ENV: Readonly<Record<SignInProvider, readonly [string, string]>> = {
  google: ['GOOGLE_CLIENT_ID', 'GOOGLE_CLIENT_SECRET'],
  x: ['X_CLIENT_ID', 'X_CLIENT_SECRET'],
  discord: ['DISCORD_CLIENT_ID', 'DISCORD_CLIENT_SECRET'],
  linkedin: ['LINKEDIN_CLIENT_ID', 'LINKEDIN_CLIENT_SECRET'],
};

/**
 * The providers whose credential pair is set in `env`, in the platform's
 * order. A pair means BOTH: an id without its secret is a start route that
 * redirects to the provider and a callback that cannot exchange the code,
 * which is a worse button than none. Server-side only (reads the
 * environment); the client reads the result from the runtime config.
 */
export function configuredSignInProviders(
  env: Readonly<Record<string, string | undefined>> = process.env,
): SignInProvider[] {
  return SIGN_IN_PROVIDERS.filter((provider) => {
    const [id, secret] = SIGN_IN_PROVIDER_CREDENTIAL_ENV[provider];
    return !!env[id]?.trim() && !!env[secret]?.trim();
  });
}

/** Server-side env reader. Safe to call only from server contexts.
 *  `consentRegime` has no env source — it is per-request, so callers that
 *  have the request headers pass it in; everything else gets the safe
 *  `'opt-in'` default. */
function readServerRuntimeConfig(regime: ConsentRegime = 'opt-in'): RuntimeConfig {
  return {
    consentRegime: regime,
    baseUrl: process.env.BASE_URL?.trim() || 'https://robutler.ai',
    sandboxOrigin: process.env.SANDBOX_ORIGIN?.trim() || null,
    sandboxPerContent: process.env.SANDBOX_PER_CONTENT === '1',
    turnstileSiteKey: process.env.TURNSTILE_SITE_KEY?.trim() || '',
    // `1` on, `0` off, unset: on under the dev server only (see the interface).
    webmcpTools:
      process.env.WEBMCP_TOOLS === '1' ||
      (process.env.WEBMCP_TOOLS !== '0' && process.env.NODE_ENV === 'development'),
    webmcpOriginTrialToken: process.env.WEBMCP_ORIGIN_TRIAL_TOKEN?.trim() || '',
    signInProviders: configuredSignInProviders(),
    analytics: {
      gaId: process.env.GA_MEASUREMENT_ID || '',
      googleAdsId: process.env.GOOGLE_ADS_ID || '',
      gadsConversions: {
        sign_up:         process.env.GADS_CONV_SIGN_UP || '',
        subscribe:       process.env.GADS_CONV_SUBSCRIBE || '',
        payment_approve: process.env.GADS_CONV_PURCHASE || '',
        first_post:      process.env.GADS_CONV_ACTIVATE || '',
        chat_send:       process.env.GADS_CONV_ENGAGE || '',
      },
      umamiUrl: process.env.UMAMI_URL || '',
      umamiWebsiteId: process.env.UMAMI_WEBSITE_ID || '',
      appVersion: process.env.BUILD_SHA || '',
    },
  };
}

const WINDOW_KEY = '__ROBUTLER_CONFIG__';

declare global {
  interface Window {
    __ROBUTLER_CONFIG__?: RuntimeConfig;
  }
}

const EMPTY: RuntimeConfig = {
  // Safe fallback before hydration: assume the stricter regime.
  consentRegime: 'opt-in',
  baseUrl: '',
  sandboxOrigin: null,
  sandboxPerContent: false,
  turnstileSiteKey: '',
  webmcpTools: false,
  webmcpOriginTrialToken: '',
  // Empty means "not known here", not "none configured": the sign-in screen
  // treats an empty list as the config not having arrived and falls back to
  // the providers every environment has always had (components/auth).
  signInProviders: [],
  analytics: {
    gaId: '',
    googleAdsId: '',
    gadsConversions: {},
    umamiUrl: '',
    umamiWebsiteId: '',
    appVersion: '',
  },
};

/**
 * Read the live runtime config. Works in both contexts:
 *   - server (SSR / route handlers / server actions): reads
 *     `process.env` directly.
 *   - client (hydrated React, event handlers, effects): reads the
 *     SSR-injected `window.__ROBUTLER_CONFIG__`. Falls back to inert
 *     defaults if the inject script didn't run (e.g. test
 *     environment that bypasses the root layout) so a render error
 *     here doesn't take down the whole page.
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window !== 'undefined') {
    return window.__ROBUTLER_CONFIG__ ?? readConfigMeta() ?? EMPTY;
  }
  return readServerRuntimeConfig();
}

/** The meta element `app/not-found.tsx` emits. See `runtimeConfigJson`. */
export const RUNTIME_CONFIG_META = 'robutler:runtime-config';

/**
 * The same config the inline script carries, as JSON for a meta element.
 *
 * WHY A META ELEMENT EXISTS AT ALL (2026-09-22). On a 404 document the root
 * layout's inline script does NOT execute, and neither does its deferred
 * `/api/runtime-config.js` repair. Both reach the client only inside the RSC
 * flight payload, serialized and escaped, and a script React inserts during
 * hydration never runs. Measured across four unrelated not-found routes on
 * dev: zero executable copies each, against one on any 200 page. Verified in
 * a browser on dev that `window.__ROBUTLER_CONFIG__` is genuinely absent
 * there while the 404 page itself renders perfectly.
 *
 * Emitting a second <script> from the 404 page was tried first and measured:
 * still nothing. It is the delivery, not the source. A meta element has no
 * such problem, because React renders it into the DOM like any other element
 * and `readConfigMeta` reads it synchronously.
 *
 * ONE TRAP WORTH RECORDING. On the LOCAL dev cluster the 404 body does not
 * render at all (blank page, `meta[name=next-error]` present), which looks
 * exactly like "the component never runs" and nearly caused this fix to be
 * deleted as dead code. That is a local dev-mode artifact. On dev the same
 * component renders its heading, copy and aurora correctly. Verify this
 * particular fix on a real deployment, never on local.
 */
export function runtimeConfigJson(
  opts: { geoCountry?: string | null; optOutMarkets?: readonly string[] | null } = {},
): string {
  return JSON.stringify(
    readServerRuntimeConfig(resolveConsentRegime(opts.geoCountry, opts.optOutMarkets)),
  );
}

/**
 * Read the config out of the meta element, once, and cache it on the global so
 * every later caller takes the fast path. Returns null when there is no meta
 * (every ordinary page, where the inline script already ran) or when its
 * content will not parse, so the caller still falls through to `EMPTY` rather
 * than throwing on top of an already-bad URL.
 */
function readConfigMeta(): RuntimeConfig | null {
  try {
    const el = document.querySelector(`meta[name="${RUNTIME_CONFIG_META}"]`);
    const raw = el?.getAttribute('content');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RuntimeConfig;
    if (!parsed || typeof parsed !== 'object') return null;
    window.__ROBUTLER_CONFIG__ = parsed;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * True when this deployment is the production apex (`robutler.ai`).
 *
 * Derived from `baseUrl` (the same value in server + client contexts)
 * rather than `NODE_ENV`, because one portal image is promoted unchanged
 * across `local → dev → staging → production` — `NODE_ENV` is `production`
 * on every deployed env, so it can't distinguish prod from dev/staging.
 * The apex host does: prod is `robutler.ai`; dev/staging/local differ.
 * Use this to gate features that should stay dark on the live site while
 * still being dogfooded everywhere else.
 */
export function isProductionRuntime(): boolean {
  try {
    return new URL(getRuntimeConfig().baseUrl).hostname === 'robutler.ai';
  } catch {
    return false;
  }
}

/**
 * Build the inline `<script>` body that the root layout injects into
 * `<head>`. Called server-side; the resulting string contains the
 * JSON config and a single assignment to the well-known window key.
 *
 * Escape strategy: JSON-stringify (handles quotes + control chars),
 * then defuse the only `<script>`-escape-relevant token `</` by
 * encoding `<` as `<`. We can't HTML-escape the whole string
 * because then JSON.parse on the client would fail.
 */
export function runtimeConfigInjectScript(
  opts: { geoCountry?: string | null; optOutMarkets?: readonly string[] | null } = {},
): string {
  const cfg = readServerRuntimeConfig(
    resolveConsentRegime(opts.geoCountry, opts.optOutMarkets),
  );
  const json = JSON.stringify(cfg).replace(/</g, '\\u003c');
  return `window.${WINDOW_KEY}=${json};`;
}
